// Wraps a raw `() => import(...)` page-import function so a stale tab that
// hits a chunk pruned by a newer deploy reloads once instead of surfacing a
// lazy-load error to the user. A one-shot sessionStorage flag prevents a
// reload loop if the reload itself lands on the same stale chunk.
import { safeLocalStorage } from '@/services/safeLocalStorage'

export const CHUNK_RELOAD_FLAG_KEY = 'nopressure:chunkReloadAttempted'

// Vite/browser lazy-import failure messages. The last is Vite's own reject
// message when a hashed CSS dependency of a chunk 404s — pruned by a newer
// deploy exactly like the JS chunks, and it surfaces as a plain Error (no
// ChunkLoadError name), so it must be matched by message.
const CHUNK_LOAD_ERROR_MESSAGES = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
  'Unable to preload CSS for',
]

function isChunkLoadError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false

  const { name, message } = error as { name?: unknown; message?: unknown }
  if (name === 'ChunkLoadError') return true
  return typeof message === 'string' && CHUNK_LOAD_ERROR_MESSAGES.some(pattern => message.includes(pattern))
}

/**
 * On a chunk-load failure, reloads the page once (returning a promise that never
 * settles, so callers never render an error mid-reload); if the flag is already
 * set — a prior reload already tried this — rethrows instead of looping. If
 * sessionStorage can't persist the one-shot flag (e.g. quota exhausted), it also
 * rethrows rather than reload: an un-persistable flag would defeat the guard and
 * loop forever, so a visible chunk-load error is the safer degrade. Any other
 * error rethrows untouched.
 *
 * The flag is intentionally NOT cleared on success: after a recovery reload the
 * app shell and landing route always load fine, and clearing on those unrelated
 * successes would re-arm the guard and let a durably-missing chunk (partial
 * deploy / CDN skew) reload on every navigation. So the guard is one reload per
 * tab session — sessionStorage-scoped, so a genuinely new tab starts fresh.
 */
export function importWithStaleChunkReload<T>(importFn: () => Promise<T>): () => Promise<T> {
  return async () => {
    try {
      return await importFn()
    } catch (error) {
      if (
        !isChunkLoadError(error) ||
        !safeLocalStorage.isAvailable('session') ||
        safeLocalStorage.get(CHUNK_RELOAD_FLAG_KEY, 'session') !== null
      ) {
        throw error
      }

      safeLocalStorage.set(CHUNK_RELOAD_FLAG_KEY, '1', 'session')
      window.location.reload()
      return new Promise<T>(() => {})
    }
  }
}
